// Overwrite guards (PLAN-agent-v2 §13 D2 and #36): a write refuses to destroy non-empty cells.
// Two guards share this detection and this payload shape, but NOT how they are released:
//   - `OccupiedCells`        — the model lifts it itself with `overwrite: true` (cell writes).
//   - `OverwriteRequiresAck` — only the user's checkbox on the card lifts it (`excel_write_block`).
// That difference is deliberate, so the codes and hints stay distinct while everything else is uniform.
// Pure helpers — the executor loads the target grid, this decides.
import type { OverwritePreview, ToolError } from '../../types/excel';
import { offsetCell, parseRange, qualify, rangeToA1 } from './a1';

export interface OccupiedCell {
  address: string;
  value: unknown;
}

/** Which guard refused the write: see the header — they differ only in who may lift them. */
export type OverwriteGuardCode = 'OccupiedCells' | 'OverwriteRequiresAck';

export interface OccupiedCellsError extends ToolError {
  code: OverwriteGuardCode;
  /** Up to MAX_LISTED occupied cells (sheet-qualified), for the error message and the model. */
  occupied: OccupiedCell[];
  /** Total number of occupied cells in the target. */
  occupied_count: number;
  /** Sheet-qualified target range that was checked; nothing in it was written. */
  address: string;
}

export const MAX_LISTED = 10;
/** Occupied addresses shown next to the acknowledgement checkbox on the card. */
export const PREVIEW_SAMPLE = 5;

/** A cell counts as occupied when it holds a formula or any value other than an empty string / null. */
export function isOccupied(value: unknown, formula: unknown): boolean {
  if (typeof formula === 'string' && formula.startsWith('=')) return true;
  return value !== null && value !== undefined && value !== '';
}

/**
 * Occupied cells of a target grid. `address` is the target range (A1, no sheet); `values` / `formulas`
 * are the grid loaded for that range (row-major). `exclude` lists A1 cells or ranges NOT to check
 * (e.g. the source of an autofill, which lies inside its destination).
 */
export function occupiedCells(values: unknown[][], formulas: unknown[][] | null | undefined, address: string, exclude: string[] = []): OccupiedCell[] {
  const range = parseRange(address);
  const topLeft = rangeToA1({ start: range.start, end: range.start });
  const excluded = new Set<string>();
  for (const ex of exclude) {
    const r = parseRange(ex);
    for (let row = r.start.row; row <= r.end.row; row++) {
      for (let col = r.start.col; col <= r.end.col; col++) excluded.add(`${row}:${col}`);
    }
  }
  const out: OccupiedCell[] = [];
  values.forEach((rowValues, ri) => {
    rowValues.forEach((value, ci) => {
      const row = range.start.row + ri;
      const col = range.start.col + ci;
      if (excluded.has(`${row}:${col}`)) return;
      if (isOccupied(value, formulas?.[ri]?.[ci])) out.push({ address: offsetCell(topLeft, ri, ci), value });
    });
  });
  return out;
}

/** Shared payload of both guards: what the write would have destroyed, and that nothing was written. */
function guardError(sheet: string, address: string, occupied: OccupiedCell[], code: OverwriteGuardCode, retryable: boolean, hint: string): OccupiedCellsError {
  const listed = occupied.slice(0, MAX_LISTED).map((c) => ({ address: qualify(sheet, c.address), value: c.value }));
  const examples = listed
    .slice(0, 3)
    .map((c) => `${c.address}=${JSON.stringify(c.value)}`)
    .join(', ');
  return {
    error: `Target contains ${occupied.length} non-empty cell${occupied.length === 1 ? '' : 's'} (e.g. ${examples}); nothing was written`,
    code,
    retryable,
    hint,
    occupied: listed,
    occupied_count: occupied.length,
    address: qualify(sheet, address),
  };
}

/** Cell writes (D2): the model may retry the same call with `overwrite: true`. */
export function occupiedError(sheet: string, address: string, occupied: OccupiedCell[]): OccupiedCellsError {
  return guardError(sheet, address, occupied, 'OccupiedCells', true, 'Choose an empty area, or pass overwrite: true only if the user asked to replace these cells (explain it in your notes)');
}

/** Block writes (#36): retrying is pointless — only the user's acknowledgement on the card lifts this. */
export function overwriteAckError(sheet: string, address: string, occupied: OccupiedCell[]): OccupiedCellsError {
  return guardError(sheet, address, occupied, 'OverwriteRequiresAck', false, 'The user must acknowledge the overwrite on the card — you cannot set overwrite_ack yourself. Otherwise use mode insert_rows or another anchor.');
}

/** Read-only pre-flight for the approval card: the count and a few addresses, never an error. */
export function overwritePreview(occupied: OccupiedCell[], address: string): OverwritePreview {
  return { cells: occupied.length, address, sample: occupied.slice(0, PREVIEW_SAMPLE).map((c) => c.address) };
}
